// ConsoleDeposit — the fix for "you can't get into the console because
// there was no screen to deposit tokens."
//
// A user-friendly page that:
//   1. Shows current AEGIS balance and per-Q&A cost
//   2. Surfaces the treasury address (copy button) + AEGIS mint + explorers
//   3. Walks the user through the 3-step deposit flow:
//        (a) Send N AEGIS to the treasury on Solana
//        (b) Paste the transaction signature back
//        (c) Submit — creates a pending deposit-intent
//   4. Shows their pending / verified / rejected intents in a live list
//   5. Explains what happens next (architect verifies → credits granted)
//
// Backend endpoints used:
//   GET  /api/console/balance          — current balance + txn log
//   POST /api/console/deposit-intent   — declare a Solana transfer
//   GET  /api/console/country-funds    — humanitarian fund transparency
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Coins,
  Copy,
  Check,
  Loader2,
  Send,
  ExternalLink,
  ShieldCheck,
  Wallet,
  Globe2,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// The Architect's Solana treasury address (mirrors backend/membership.py::TARGET_WALLET).
// Deposits routed here plus per-country humanitarian fund accounting.
const TREASURY_ADDRESS = "GfRqh1hDWuWapbKVshPaW2Y5dTvpxR3wx98BWj7FR4GU";
const AEGIS_MINT = "28qe78vzZMW6uimVGkws8kpQwkJTwuvLfxnc4a83pump";

// ISO-3166 country codes — kept short. UNSPECIFIED is the default for
// users who prefer not to earmark. Full list is the top-populated
// countries; anything else can be typed manually.
const COMMON_COUNTRIES = [
  { code: "UNSPECIFIED", label: "Unspecified · general fund" },
  { code: "USA", label: "United States (USA)" },
  { code: "IND", label: "India (IND)" },
  { code: "GBR", label: "United Kingdom (GBR)" },
  { code: "CAN", label: "Canada (CAN)" },
  { code: "AUS", label: "Australia (AUS)" },
  { code: "DEU", label: "Germany (DEU)" },
  { code: "FRA", label: "France (FRA)" },
  { code: "JPN", label: "Japan (JPN)" },
  { code: "BRA", label: "Brazil (BRA)" },
  { code: "MEX", label: "Mexico (MEX)" },
  { code: "NGA", label: "Nigeria (NGA)" },
  { code: "PAK", label: "Pakistan (PAK)" },
  { code: "IDN", label: "Indonesia (IDN)" },
  { code: "PHL", label: "Philippines (PHL)" },
];

const AMOUNT_PRESETS = [10, 25, 50, 100, 250];

function CopyField({ label, value, testid, mono = true }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5 font-mono">
        {label}
      </div>
      <button
        onClick={copy}
        data-testid={testid}
        className={`w-full flex items-center gap-2 px-3 py-2 border border-zinc-800 hover:border-emerald-500/60 bg-black/60 text-left group ${
          mono ? "font-mono" : ""
        }`}
      >
        <span className="text-sm text-zinc-100 break-all flex-1">{value}</span>
        {done ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
        ) : (
          <Copy className="h-3.5 w-3.5 shrink-0 text-zinc-500 group-hover:text-emerald-300" />
        )}
      </button>
    </div>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "verified"
      ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/[0.05]"
      : status === "rejected"
      ? "border-rose-500/60 text-rose-300 bg-rose-500/[0.05]"
      : "border-amber-500/60 text-amber-300 bg-amber-500/[0.05]";
  const label =
    status === "verified"
      ? "verified · credited"
      : status === "rejected"
      ? "rejected"
      : "pending verify";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[9.5px] uppercase tracking-[0.28em] ${cls}`}
      data-testid={`intent-status-${status}`}
    >
      {status === "pending_verification" ? <Clock className="h-2.5 w-2.5" /> : null}
      {label}
    </span>
  );
}

export default function ConsoleDeposit() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [balance, setBalance] = useState(null);
  const [spendableAegis, setSpendableAegis] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [intents, setIntents] = useState([]);

  const [amount, setAmount] = useState(25);
  const [country, setCountry] = useState("UNSPECIFIED");
  const [txSig, setTxSig] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [spending, setSpending] = useState(false);
  const [buyingBuilder, setBuyingBuilder] = useState(false);

  const signedIn = Boolean(user);

  const loadBalance = useCallback(async () => {
    if (!signedIn) {
      setBalanceLoading(false);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await fetch(`${API}/api/console/balance`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`balance ${res.status}`);
      const data = await res.json();
      setBalance(data?.balance ?? 0);
      setSpendableAegis(data?.spendable_aegis ?? 0);
      const log = data?.txn_log || [];
      setIntents(
        log
          .filter((t) =>
            (t.source || "").toString().includes("deposit") ||
            (t.type || "").toString().includes("deposit"),
          )
          .slice(0, 20),
      );
    } catch (e) {
      setBalance(0);
      setSpendableAegis(0);
    } finally {
      setBalanceLoading(false);
    }
  }, [signedIn]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  const spendOnSite = async () => {
    if (!signedIn) {
      toast.error("Sign in required.");
      navigate("/signin");
      return;
    }
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Amount must be a positive number.");
      return;
    }
    setSpending(true);
    try {
      const res = await fetch(`${API}/api/console/spend-aegis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: Math.round(n),
          country_code: country,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      toast.success(`+${data.grant.amount} console credits granted`, {
        description: `Debited from your earned AEGIS. New balance: ${data.new_balance}.`,
      });
      loadBalance();
    } catch (err) {
      toast.error("On-site spend failed", {
        description: String(err?.message || err),
      });
    } finally {
      setSpending(false);
    }
  };

  const buyBuilderWithAegis = async () => {
    if (!signedIn) {
      toast.error("Sign in required.");
      return;
    }
    setBuyingBuilder(true);
    try {
      const res = await fetch(`${API}/api/membership/spend-aegis-for-builder`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      toast.success("Builder tier unlocked for 30 days", {
        description: `100 AEGIS debited. Expires ${new Date(data.grant.expires_at).toLocaleDateString()}.`,
      });
      loadBalance();
    } catch (err) {
      toast.error("Builder upgrade failed", {
        description: String(err?.message || err),
      });
    } finally {
      setBuyingBuilder(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!signedIn) {
      toast.error("Sign in required.");
      navigate("/signin");
      return;
    }
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Amount must be a positive number.");
      return;
    }
    if (!txSig || txSig.trim().length < 20) {
      toast.error("Paste your Solana transaction signature (typically 88 chars).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/console/deposit-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          amount: Math.round(n),
          country_code: country,
          solana_tx_sig: txSig.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data?.detail?.message || data?.detail || data?.message || `HTTP ${res.status}`,
        );
      }
      toast.success("Deposit intent submitted", {
        description:
          "Waiting for architect verification. Credits will appear here once confirmed.",
      });
      setTxSig("");
      setAmount(25);
      loadBalance();
    } catch (err) {
      toast.error("Deposit submission failed", {
        description: String(err?.message || err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = useMemo(
    () => signedIn && txSig.trim().length >= 20 && Number(amount) > 0 && !submitting,
    [signedIn, txSig, amount, submitting],
  );

  return (
    <div
      className="min-h-screen bg-[#09090b] text-zinc-100"
      data-testid="console-deposit-page"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        <Link
          to="/console"
          data-testid="deposit-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> back to console
        </Link>

        <div className="border-b border-zinc-900 pb-6 mb-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-3 font-mono">
            <Coins className="h-3 w-3" /> AEGIS console · fund
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05]">
            Fund your console with AEGIS you&apos;ve earned.
          </h1>
          <p className="mt-5 text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl">
            Every question to the Sentinel costs <span className="text-emerald-300 font-mono">1 AEGIS</span>.
            You earn AEGIS on every verified truth (51% of every mint goes to the discoverer).
            Spend your earned balance directly here — no external wallet, no leaving the site.
          </p>
        </div>

        {/* Balance + spendable AEGIS */}
        <div
          className="grid gap-3 sm:grid-cols-2 mb-4"
          data-testid="deposit-balance-card"
        >
          <div className="border border-zinc-800 bg-[#0b0b0d] px-5 py-4 flex items-center gap-4">
            <Wallet className="h-5 w-5 text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-mono">
                Console credits
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className="text-3xl font-mono tabular-nums text-zinc-100"
                  data-testid="deposit-balance-value"
                >
                  {balanceLoading ? "…" : (balance ?? 0)}
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                  · 1 per Q&amp;A
                </span>
              </div>
            </div>
          </div>
          <div className="border border-emerald-500/40 bg-emerald-500/[0.04] px-5 py-4 flex items-center gap-4">
            <Coins className="h-5 w-5 text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.32em] text-emerald-300 font-mono">
                Your earned AEGIS
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className="text-3xl font-mono tabular-nums text-emerald-200"
                  data-testid="deposit-spendable-aegis"
                >
                  {balanceLoading ? "…" : (spendableAegis ?? 0)}
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-mono">
                  · spendable
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* On-site AEGIS spend form — the primary path */}
        <div
          className="border border-emerald-500/50 bg-emerald-500/[0.03] p-5 mb-6"
          data-testid="deposit-onsite-spend"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-2 font-mono flex items-center gap-2">
            <Coins className="h-3 w-3" /> Fund from your earned AEGIS · recommended
          </div>
          <p className="text-[12.5px] text-zinc-300 leading-relaxed mb-4">
            Spend your on-site AEGIS balance directly — nothing leaves the site. Coins
            are debited oldest-first from your earned holdings, credits appear instantly.
          </p>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] items-end">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-1.5 font-mono">
                Amount · AEGIS
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {AMOUNT_PRESETS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(v)}
                    className={`px-3 py-1.5 border font-mono text-[10.5px] uppercase tracking-[0.28em] ${
                      Number(amount) === v
                        ? "border-emerald-500 text-emerald-200 bg-emerald-500/[0.08]"
                        : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                    }`}
                    data-testid={`deposit-preset-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-black/60 border border-zinc-800 focus:border-emerald-500/60 outline-none text-sm text-zinc-100 px-3 py-2 font-mono"
                data-testid="deposit-amount-input"
              />
            </div>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="bg-black/60 border border-zinc-800 focus:border-emerald-500/60 outline-none text-sm text-zinc-100 px-3 py-2 font-mono"
              data-testid="deposit-country-select"
            >
              {COMMON_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={spendOnSite}
              disabled={
                !signedIn ||
                spending ||
                Number(amount) <= 0 ||
                (spendableAegis !== null && Number(amount) > spendableAegis)
              }
              className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500 hover:text-zinc-950 font-mono text-[11px] uppercase tracking-[0.3em] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="deposit-onsite-spend-button"
            >
              {spending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              spend
            </button>
          </div>
          {spendableAegis !== null && Number(amount) > spendableAegis ? (
            <p className="mt-3 text-[11px] text-amber-300">
              You have {spendableAegis} spendable AEGIS. Earn more by verifying
              truths, or lower the amount.
            </p>
          ) : (
            <p className="mt-3 text-[11px] text-zinc-500">
              Balance after spend: <span className="font-mono text-zinc-200">
                {(spendableAegis ?? 0) - Number(amount || 0)}
              </span> AEGIS · Routes to <span className="text-emerald-300 font-mono">
                {country}
              </span> humanitarian fund.
            </p>
          )}
        </div>

        {/* Builder tier · 100 AEGIS one-click */}
        <div
          className="border border-amber-500/50 bg-amber-500/[0.03] p-5 mb-6 flex flex-wrap items-center gap-4"
          data-testid="deposit-buy-builder"
        >
          <ShieldCheck className="h-6 w-6 text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-1 font-mono">
              Builder tier · 30 days
            </div>
            <p className="text-[12.5px] text-zinc-300 leading-relaxed">
              Spend <span className="text-amber-300 font-mono">100 AEGIS</span> from
              your earned holdings to unlock 25 questions/day for 30 days.
              Same self-funding loop — no wallet.
            </p>
          </div>
          <button
            type="button"
            onClick={buyBuilderWithAegis}
            disabled={
              !signedIn ||
              buyingBuilder ||
              (spendableAegis !== null && spendableAegis < 100)
            }
            className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500 text-amber-200 hover:bg-amber-500 hover:text-zinc-950 font-mono text-[11px] uppercase tracking-[0.3em] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deposit-buy-builder-button"
          >
            {buyingBuilder ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5" />
            )}
            unlock builder · 100 aegis
          </button>
        </div>

        {signedIn ? (
          <div className="mb-6 flex justify-end">
            <Link
              to="/console"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 font-mono text-[10px] uppercase tracking-[0.3em]"
              data-testid="deposit-goto-console"
            >
              open console <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        ) : null}

        {/* AEGIS-only reinforcement (still true for those choosing on-chain path) */}
        <details
          className="border border-zinc-800 bg-[#0b0b0d] px-4 py-3 mb-6 group"
          data-testid="deposit-onchain-details"
        >
          <summary className="cursor-pointer text-[10px] uppercase tracking-[0.28em] text-zinc-500 hover:text-zinc-300 font-mono">
            Advanced · fund from an external Solana wallet
          </summary>
          <div className="mt-4 space-y-4">
            <div
              className="border border-emerald-500/50 bg-emerald-500/[0.04] px-4 py-3 flex items-start gap-3"
              data-testid="deposit-aegis-only-callout"
            >
              <Coins className="h-4 w-4 shrink-0 mt-0.5 text-emerald-400" />
              <div className="text-[12.5px] text-zinc-200 leading-relaxed">
                <span className="text-emerald-300 font-mono uppercase tracking-[0.28em] text-[10.5px] block mb-1">
                  AEGIS token only
                </span>
                Only send AEGIS. Do not send SOL, USDC, or any other token — those
                transfers cannot be credited here.
              </div>
            </div>

            {!signedIn && !authLoading ? (
          <div
            className="border border-amber-500/50 bg-amber-500/[0.05] px-5 py-4 mb-6 text-sm text-amber-200"
            data-testid="deposit-signin-notice"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-1">
              Sign-in required
            </div>
            <p>
              Deposit credits are per-account. Please{" "}
              <Link to="/signin" className="text-amber-100 underline">
                sign in
              </Link>{" "}
              first — the treasury address below still applies once you do.
            </p>
          </div>
        ) : null}

        {/* Step 1 — send on Solana */}
        <div className="mb-6" data-testid="deposit-step-1">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center h-6 w-6 border border-emerald-500/60 text-emerald-300 font-mono text-[11px]">
              1
            </span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-mono">
              Send AEGIS to the treasury on Solana
            </span>
          </div>
          <div className="border border-zinc-800 bg-[#0b0b0d] p-5 space-y-4">
            <CopyField
              label="Treasury address · Solana mainnet"
              value={TREASURY_ADDRESS}
              testid="deposit-copy-treasury"
            />
            <CopyField
              label="AEGIS SPL mint"
              value={AEGIS_MINT}
              testid="deposit-copy-mint"
            />
            <div className="flex flex-wrap gap-3 pt-1">
              <a
                href={`https://solscan.io/token/${AEGIS_MINT}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-emerald-300 hover:text-emerald-100 font-mono"
                data-testid="deposit-solscan-link"
              >
                open on solscan <ExternalLink className="h-3 w-3" />
              </a>
              <a
                href={`https://pump.fun/coin/${AEGIS_MINT}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-emerald-300 hover:text-emerald-100 font-mono"
                data-testid="deposit-pumpfun-link"
              >
                open on pump.fun <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Use any Solana wallet (Phantom, Solflare, Backpack). Send the AEGIS
              token — <strong>not SOL, not USDC</strong> — to the treasury address
              above. The amount you send should match what you enter in Step 3.
            </p>
          </div>
        </div>

        {/* Step 2 — country selector */}
        <div className="mb-6" data-testid="deposit-step-2">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center h-6 w-6 border border-emerald-500/60 text-emerald-300 font-mono text-[11px]">
              2
            </span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-mono">
              Earmark the humanitarian route
            </span>
          </div>
          <div className="border border-zinc-800 bg-[#0b0b0d] p-5">
            <div className="flex items-center gap-2 mb-2">
              <Globe2 className="h-3.5 w-3.5 text-zinc-500" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                Country fund · optional
              </span>
            </div>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full bg-black/60 border border-zinc-800 focus:border-emerald-500/60 outline-none text-sm text-zinc-100 px-3 py-2 font-mono"
              data-testid="deposit-country-select"
            >
              {COMMON_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
              Your deposit routes 1:1 into the selected country&apos;s emergency fund
              accounting. All country fund balances are public at{" "}
              <Link to="/reserve" className="text-emerald-300 hover:text-emerald-100">
                /reserve
              </Link>
              .
            </p>
          </div>
        </div>

        {/* Step 3 — declare + submit */}
        <div className="mb-8" data-testid="deposit-step-3">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center h-6 w-6 border border-emerald-500/60 text-emerald-300 font-mono text-[11px]">
              3
            </span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-mono">
              Declare your transaction
            </span>
          </div>
          <form onSubmit={submit} className="border border-zinc-800 bg-[#0b0b0d] p-5 space-y-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5 font-mono">
                Amount · AEGIS
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {AMOUNT_PRESETS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(v)}
                    className={`px-3 py-1.5 border font-mono text-[10.5px] uppercase tracking-[0.28em] ${
                      Number(amount) === v
                        ? "border-emerald-500 text-emerald-200 bg-emerald-500/[0.08]"
                        : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                    }`}
                    data-testid={`deposit-preset-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-black/60 border border-zinc-800 focus:border-emerald-500/60 outline-none text-sm text-zinc-100 px-3 py-2 font-mono"
                data-testid="deposit-amount-input"
              />
              <p className="mt-1 text-[10.5px] text-zinc-600 font-mono">
                = {Number(amount) || 0} questions to the Sentinel
              </p>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5 font-mono">
                Solana transaction signature
              </div>
              <textarea
                value={txSig}
                onChange={(e) => setTxSig(e.target.value)}
                rows={3}
                placeholder="paste your tx signature (from Phantom/Solflare/Solscan after the send)"
                className="w-full bg-black/60 border border-zinc-800 focus:border-emerald-500/60 outline-none text-sm text-zinc-100 p-2.5 font-mono placeholder:text-zinc-700 break-all"
                data-testid="deposit-tx-sig-input"
              />
              <p className="mt-1 text-[10.5px] text-zinc-600">
                Typically 87–88 characters, e.g.{" "}
                <span className="font-mono">5xj2kNv…8Uy</span>. Grab it from the
                transaction detail view in your wallet.
              </p>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500 hover:text-zinc-950 font-mono text-[11px] uppercase tracking-[0.32em] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="deposit-submit-button"
            >
              {submitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              {submitting ? "submitting…" : "submit deposit intent"}
            </button>
            <p className="text-[10.5px] text-zinc-500 leading-relaxed">
              Your intent lands in the queue as{" "}
              <span className="text-amber-300 font-mono">pending verification</span>.
              The Architect (or a future on-chain indexer) confirms the transaction
              on Solana and grants your credits. Balance updates on this page as
              soon as verification lands.
            </p>
          </form>
        </div>
          </div>
        </details>

        {/* Recent intents (if any) */}
        {intents.length > 0 ? (
          <div className="mb-8" data-testid="deposit-recent-intents">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3 font-mono flex items-center gap-2">
              <ShieldCheck className="h-3 w-3" /> your recent activity
            </div>
            <div className="border border-zinc-800 bg-[#0b0b0d] divide-y divide-zinc-900">
              {intents.map((t, i) => (
                <div
                  key={t.txn_id || i}
                  className="px-4 py-3 flex flex-wrap items-center gap-2 text-[11px]"
                  data-testid={`deposit-intent-row-${i}`}
                >
                  <span className="font-mono tabular-nums text-emerald-300">
                    +{t.amount ?? 0}
                  </span>
                  <span className="text-zinc-500 font-mono uppercase tracking-[0.2em] text-[10px]">
                    AEGIS · {t.source || t.type || "deposit"}
                  </span>
                  <span className="ml-auto text-zinc-600 font-mono text-[10px]">
                    {t.timestamp ? new Date(t.timestamp).toLocaleString() : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <p className="text-[10.5px] text-zinc-600 leading-relaxed font-mono">
          If you sent tokens but forgot to submit the intent, no funds are lost —
          just paste the tx signature above. Architect can back-fill from any
          signature received at the treasury.
        </p>
      </div>
    </div>
  );
}
